import type { IChannelAllocation } from '@feathr/blackbox';

export const MIN_SPLIT = 5;
export const MAX_SPLIT = 95;

/** Both Display (TTD) and Meta require at least $2/day on their channel. */
export const CHANNEL_MIN_DAILY_BUDGET = 2;

/**
 * Find the percentage for a given channel in the allocations array.
 *
 * @param allocations - Array of channel allocation objects.
 * @param channel - The channel identifier to look up.
 * @returns The percentage for the channel, or undefined if not found.
 */
export function getAllocationPercentage(
  allocations: IChannelAllocation[] | undefined,
  channel: IChannelAllocation['channel'],
): number | undefined {
  return allocations?.find((a) => a.channel === channel)?.percentage;
}

/**
 * Calculate recommended split from audience reach data.
 *
 * @param displayReach - Number of reachable cookies (display channel).
 * @param metaReach - Number of reachable emails (meta channel).
 * @returns Object with tradedesk and meta percentages summing to 100.
 */
export function calculateRecommendedSplit(
  displayReach: number,
  metaReach: number,
): { tradedesk: number; meta: number } {
  const totalReach = displayReach + metaReach;
  if (totalReach === 0) {
    return { tradedesk: 50, meta: 50 };
  }
  const rawTradedesk = Math.round((displayReach / totalReach) * 100);
  const clampedTradedesk = Math.max(MIN_SPLIT, Math.min(MAX_SPLIT, rawTradedesk));
  return { tradedesk: clampedTradedesk, meta: 100 - clampedTradedesk };
}

/**
 * Calculate per-channel dollar amounts from total budget and display percentage.
 *
 * @param totalBudget - Total campaign budget in dollars.
 * @param displayPercent - Integer percentage allocated to display (0-100).
 * @returns Object with displayBudget and metaBudget that sum to totalBudget.
 */
export function calculateChannelBudgets(
  totalBudget: number,
  displayPercent: number,
): { displayBudget: number; metaBudget: number } {
  const displayBudget = Math.round(((totalBudget * displayPercent) / 100) * 100) / 100;
  const metaBudget = Math.round((totalBudget - displayBudget) * 100) / 100;
  return { displayBudget, metaBudget };
}
